/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createSlice, AnyAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { SET_ASSISTANT_ID } from '@ibm-watson/shared/modules/ia/store/session/actionTypes';
import { ApiStatus } from '@ibm-watson/shared/ts';
import type { IntegrationSchema } from '@ibm-watson/shared/ts/integration';
import {
  initialState as phoneInitialState,
  reducers as phoneReducers,
  extraReducers as phoneExtraReducers
} from '../phone/reducer';
import {
  initialState as segmentInitialState,
  reducers as segmentReducers,
  extraReducers as segmentExtraReducers
} from '../segment/reducer';
import {
  initialState as serviceDeskInitialState,
  reducers as serviceDeskReducers,
  extraReducers as serviceDeskExtraReducers
} from '../service-desks/reducer';
import {
  createIntegration,
  deleteIntegration,
  disableSaveCreateButton,
  listIntegrations,
  loadCurrentIntegration,
  resetAllIntegrations,
  resetCurrentIntegration,
  setDraftIntegrationEnableSecretFields,
  setIntegrationInitializing,
  subscribeIntegration,
  updateIntegration
} from './actions';
import { integrationsAdapter } from './adapter';
import { extraReducers as classicIntegrationsExtraReducers } from './classic/reducer';

const initialState = integrationsAdapter.getInitialState({
  listIntegrations: {
    status: ApiStatus.IDLE,
    fetched: null,
    error: null,
    pendingCallStatus: {}
  },

  createIntegration: {
    status: ApiStatus.IDLE,
    error: null
  },

  loadIntegration: {
    status: ApiStatus.IDLE,
    error: null
  },

  updateIntegration: {
    status: ApiStatus.IDLE,
    error: null
  },

  deleteIntegration: {
    status: ApiStatus.IDLE,
    error: null
  },

  subscribeIntegration: {
    status: ApiStatus.IDLE,
    error: null
  },

  describeIntegration: {
    status: ApiStatus.IDLE,
    error: null
  },

  currentIntegrationId: null, // to do: deprecate
  draftIntegration: {
    integration: null,
    name: {
      valid: null,
      dirty: null
    },
    settings: {
      valid: null,
      dirty: null
    },
    // Used to track whether the integration has been created in the
    // back end, before the user clicks Finish.
    integration_prematurely_created: null,
    disable_save_create_button: null,

    // Indicates whether the draft integration is in a no-parsing,
    // initializing, or loading state.
    integration_initializing: false,

    // If private.secrets fields show be enabled and required.
    enable_secret_fields: null,

    // Denotes whether useChannelForm should invoke a reset. If true,
    // then useChannelForm will reset the integration's form-watch
    // and sync it with the draft integration.
    reset_integration_form: null,

    // used by old IA only
    channel_type: null,
    step: null,
    show_overlay: null,
    legacy_service_desk: null
  },

  phone: phoneInitialState,

  serviceDesk: serviceDeskInitialState,

  segment: segmentInitialState
});

export const computeState = (updates?: any): any => ({
  ..._.cloneDeep(initialState),
  ...updates
});

const slice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    ...phoneReducers,
    ...serviceDeskReducers,
    ...segmentReducers,
    defineNewIntegration(state, action) {
      const { integration, settingsIsValid } = action.payload;

      state.draftIntegration.integration = integration;
      state.draftIntegration.name.valid = true;
      state.draftIntegration.name.dirty = true;
      state.draftIntegration.settings.valid = settingsIsValid ?? true;
      state.draftIntegration.settings.dirty = false;
    },
    updateDraftIntegrationName(state, action) {
      const { value, isValid, isDirty } = action.payload;

      if (!state.draftIntegration.integration) {
        state.draftIntegration.integration = {};
      }
      state.draftIntegration.integration.name = value;

      state.draftIntegration.name.valid = isValid;
      state.draftIntegration.name.dirty = isDirty;
    },
    updateDraftIntegrationType(state, action) {
      const { value } = action.payload;

      if (!state.draftIntegration.integration) {
        state.draftIntegration.integration = {};
      }
      state.draftIntegration.integration.type = value;
    },
    updateDraftIntegrationSettings(state, action) {
      const { settings, isValid, isDirty } = action.payload;

      if (state.draftIntegration.integration) {
        // We need to merge the contents of the integration
        // rather than overwrite them.
        state.draftIntegration.integration.settings = _.mergeWith(
          state.draftIntegration.integration?.settings,
          settings,
          // Array contents of a Draft Instance override the
          // ones in the currentInstance and they are not merged.
          (objValue, srcValue) => {
            if (_.isArray(objValue)) {
              return srcValue;
            }
          }
        );
      }

      if (isValid !== undefined) {
        state.draftIntegration.settings.valid = isValid;
      }

      if (isDirty !== undefined) {
        state.draftIntegration.settings.dirty = isDirty;
      }
    }
  },
  extraReducers: builder => {
    builder
      // LIST INTEGRATIONS
      .addCase(listIntegrations.pending, (state, action) => {
        state.listIntegrations.status = ApiStatus.LOADING;
        state.listIntegrations.error = null;
        state.listIntegrations.pendingCallStatus[action.meta.arg] = Date.now();
      })
      .addCase(listIntegrations.fulfilled, (state, action) => {
        state.listIntegrations.status = ApiStatus.SUCCEEDED;
        state.listIntegrations.fetched = Date.now();
        const key = action.meta.arg;
        delete state.listIntegrations.pendingCallStatus[key];
        state.listIntegrations.error = null;

        integrationsAdapter.setAll(state, action.payload);
      })
      .addCase(listIntegrations.rejected, (state, action) => {
        const key = action.meta.arg;
        delete state.listIntegrations.pendingCallStatus[key];
        state.listIntegrations.status = ApiStatus.ERROR;
        state.listIntegrations.error = action.error;
      })
      // LOAD INTEGRATION
      .addCase(loadCurrentIntegration.pending, (state, _) => {
        state.loadIntegration.status = ApiStatus.LOADING;
        state.loadIntegration.error = null;
      })
      .addCase(loadCurrentIntegration.fulfilled, (state, action) => {
        state.loadIntegration.status = ApiStatus.SUCCEEDED;
        state.loadIntegration.error = null;

        state.draftIntegration.integration = action.payload;

        state.draftIntegration.name.valid = true;
        state.draftIntegration.name.dirty = false;
        state.draftIntegration.settings.valid = true;
        state.draftIntegration.settings.dirty = false;

        // Make sure we update the existing integration we already have
        integrationsAdapter.updateOne(state, {
          id: action.payload.integration_id,
          changes: action.payload
        });
      })
      .addCase(loadCurrentIntegration.rejected, (state, action) => {
        state.loadIntegration.status = ApiStatus.ERROR;
        state.loadIntegration.error = action.error;

        state.currentIntegrationId = null;
        state.draftIntegration.integration = null;
      })
      // UPDATE INTEGRATION
      .addCase(updateIntegration.pending, (state, _) => {
        state.updateIntegration.status = ApiStatus.LOADING;
        state.updateIntegration.error = null;
      })
      .addCase(updateIntegration.fulfilled, (state, action) => {
        state.updateIntegration.status = ApiStatus.SUCCEEDED;
        state.updateIntegration.error = null;

        // make sure we're clearing away private settings after
        // saving so we don't keep track of sensitive values
        if (state.draftIntegration.integration?.settings?.private?.secrets) {
          _.set(
            state.draftIntegration.integration?.settings,
            'private.secrets',
            undefined
          );
        }

        // integrations in entity list should not contain private fields
        integrationsAdapter.upsertOne(
          state,
          _.omit(action.payload, [
            'settings.private.secrets'
          ]) as IntegrationSchema
        );

        // invalidate listIntegrations cache
        state.listIntegrations.fetched = null;
      })
      .addCase(updateIntegration.rejected, (state, action) => {
        state.updateIntegration.status = ApiStatus.ERROR;
        state.updateIntegration.error = action.error;
      })
      // CREATE INTEGRATION
      .addCase(createIntegration.pending, (state, action) => {
        state.createIntegration.status = ApiStatus.LOADING;
        state.createIntegration.error = null;

        if (action.meta?.arg?.markAsPremature === true) {
          state.draftIntegration.integration_prematurely_created = true;
        }
      })
      .addCase(createIntegration.fulfilled, (state, action) => {
        state.createIntegration.status = ApiStatus.SUCCEEDED;
        state.createIntegration.error = null;

        // make sure we're clearing away private settings after
        // saving so we don't keep track of sensitive values
        if (state.draftIntegration.integration?.settings?.private?.secrets) {
          _.set(
            state.draftIntegration.integration?.settings,
            'private.secrets',
            undefined
          );
        }

        // integrations in entity list should not contain private fields
        integrationsAdapter.addOne(
          state,
          _.omit(action.payload, [
            'settings.private.secrets'
          ]) as IntegrationSchema
        );

        // invalidate listIntegrations cache
        state.listIntegrations.fetched = null;
      })
      .addCase(createIntegration.rejected, (state, action) => {
        state.createIntegration.status = ApiStatus.ERROR;
        state.createIntegration.error = action.error;
      })
      // DELETE INTEGRATION
      .addCase(deleteIntegration.pending, (state, _) => {
        state.deleteIntegration.status = ApiStatus.LOADING;
        state.deleteIntegration.error = null;
      })
      .addCase(deleteIntegration.fulfilled, (state, action) => {
        const { integrationId, serviceDeskId } = action.payload;

        state.deleteIntegration.status = ApiStatus.SUCCEEDED;
        state.deleteIntegration.error = null;

        integrationsAdapter.removeOne(state, integrationId);
        if (!_.isNil(serviceDeskId)) {
          integrationsAdapter.removeOne(state, serviceDeskId);
        }

        // invalidate listIntegrations cache
        state.listIntegrations.fetched = null;
      })
      .addCase(deleteIntegration.rejected, (state, action) => {
        state.deleteIntegration.status = ApiStatus.ERROR;
        state.deleteIntegration.error = action.error;
      })
      // SUBSCRIBE
      .addCase(subscribeIntegration.pending, (state, _) => {
        state.subscribeIntegration.status = ApiStatus.LOADING;
        state.subscribeIntegration.error = null;
      })
      .addCase(subscribeIntegration.fulfilled, (state, action) => {
        state.subscribeIntegration.status = ApiStatus.SUCCEEDED;
        state.subscribeIntegration.error = null;
        // If a response data is returned, merge that with the draft
        // integration settings.
        if (
          !action.meta.arg.mergeResponse === false &&
          !_.isEmpty(action.payload) &&
          !_.isEmpty(state.draftIntegration?.integration)
        ) {
          state.draftIntegration.integration.settings = _.merge(
            state.draftIntegration.integration?.settings,
            action.payload
          );
          state.draftIntegration.settings.valid = true;
          state.draftIntegration.settings.dirty = true;
        }
      })
      .addCase(subscribeIntegration.rejected, (state, action) => {
        state.subscribeIntegration.status = ApiStatus.ERROR;
        state.subscribeIntegration.error = action.error;
      })
      .addCase(disableSaveCreateButton.fulfilled, (state, action) => {
        state.draftIntegration.disable_save_create_button = action.payload;
      })
      .addCase(setDraftIntegrationEnableSecretFields, (state, action) => {
        state.draftIntegration.enable_secret_fields = action.payload;
      })
      .addCase(setIntegrationInitializing, (state, action) => {
        state.draftIntegration.integration_initializing = action.payload;
      })
      // RESET
      .addCase(resetCurrentIntegration, (state, _) => {
        // Don't reset if another invocation is in the middle of updating
        if (state.updateIntegration.status == ApiStatus.LOADING) {
          return;
        }

        state.loadIntegration.status = ApiStatus.IDLE;
        state.createIntegration.status = ApiStatus.IDLE;
        state.updateIntegration.status = ApiStatus.IDLE;
        state.deleteIntegration.status = ApiStatus.IDLE;

        state.draftIntegration.integration = null;
        state.draftIntegration.name.valid = null;
        state.draftIntegration.name.dirty = null;
        state.draftIntegration.settings.valid = null;
        state.draftIntegration.settings.dirty = null;

        state.draftIntegration.integration_prematurely_created = null;
        state.draftIntegration.disable_save_create_button = false;
        state.draftIntegration.integration_initializing = false;
        state.draftIntegration.enable_secret_fields = null;
        state.draftIntegration.reset_integration_form = null;

        // invalid list integration caching
        state.listIntegrations.fetched = null;

        // reset subscribe integration status
        state.subscribeIntegration.status = ApiStatus.IDLE;
        state.subscribeIntegration.error = null;
        // state.intelepeer.isIntelepeer = null;
        // state.intelepeer.status = ApiStatus.IDLE;
        // state.intelepeer.state = LoaderStates.ACTIVE;
      })
      .addCase(resetAllIntegrations, (state, _) => {
        // Don't reset if another invocation is in the middle of updating
        if (state.updateIntegration.status == ApiStatus.LOADING) {
          return;
        }

        state.listIntegrations.status = ApiStatus.IDLE;
        state.loadIntegration.status = ApiStatus.IDLE;
        state.createIntegration.status = ApiStatus.IDLE;
        state.updateIntegration.status = ApiStatus.IDLE;
        state.deleteIntegration.status = ApiStatus.IDLE;

        state.draftIntegration.integration = null;
        state.draftIntegration.name.valid = null;
        state.draftIntegration.name.dirty = null;
        state.draftIntegration.settings.valid = null;
        state.draftIntegration.settings.dirty = null;

        state.draftIntegration.integration_prematurely_created = null;
        state.draftIntegration.disable_save_create_button = false;
        state.draftIntegration.integration_initializing = false;
        state.draftIntegration.enable_secret_fields = null;
        state.draftIntegration.reset_integration_form = null;

        // reset subscribe integration status
        state.subscribeIntegration.status = ApiStatus.IDLE;
        state.subscribeIntegration.error = null;

        // invalid list integration caching
        state.listIntegrations.fetched = null;

        // remove all integrations from entities
        integrationsAdapter.removeAll(state);
      })
      .addCase<string, AnyAction>(SET_ASSISTANT_ID, (state, action) => {
        if (action.assistantId) {
          state.listIntegrations.fetched = null;
        }
      });

    classicIntegrationsExtraReducers(builder);
    phoneExtraReducers(builder);
    serviceDeskExtraReducers(builder);
    segmentExtraReducers(builder);
  }
});

export default slice.reducer;

export const {
  defineNewIntegration,
  defineNewServiceDesk,
  updateDraftIntegrationName,
  updateDraftIntegrationType,
  updateDraftIntegrationSettings,
  updateServiceDeskSettings
} = slice.actions;
